import { AppError } from '@sunucupark/client-core';
import { QueryClient } from '@tanstack/react-query';

const THIRTY_SECONDS = 30_000;
const ONE_DAY = 24 * 3_600_000;

/** Failures that will not get better by asking again. */
const FINAL_CODES: ReadonlySet<string> = new Set([
  'NOT_FOUND',
  'VALIDATION_ERROR',
  'FORBIDDEN',
  'OFFLINE',
  'ABORTED',
]);

export interface QueryClientOptions {
  /** Seconds a fetched answer counts as fresh; the mail list overrides it per query. */
  staleTimeMs?: number;
  gcTimeMs?: number;
  /** The browser refetches when the tab comes back; React Native does not. */
  refetchOnWindowFocus?: boolean;
}

/**
 * The shared TanStack Query setup: the same retry rules on every client, so a
 * session that ended, a rejected request or an offline device never turn into a
 * retry storm (ADR 0006).
 */
export function createQueryClient(options: QueryClientOptions = {}): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: options.staleTimeMs ?? THIRTY_SECONDS,
        gcTime: options.gcTimeMs ?? ONE_DAY,
        retry: (failureCount, error) => {
          const appError = AppError.from(error);
          if (appError.terminatesSession || FINAL_CODES.has(appError.code)) return false;
          // The gateway already fails fast while the mail server is down; one retry is enough.
          if (
            appError.code === 'MAIL_SERVER_UNAVAILABLE' ||
            appError.code === 'SERVICE_UNAVAILABLE'
          )
            return failureCount < 1;
          return failureCount < 2;
        },
        refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
        refetchOnReconnect: true,
        networkMode: 'offlineFirst',
      },
      mutations: { retry: 0, networkMode: 'offlineFirst' },
    },
  });
}
